<script>
  import { createEventDispatcher, onMount } from "svelte";
  import { ignore, makeWebsiteRequest } from "../../Common/tools";
  import Table from "../../Table/Table.svelte";
  import { AppState } from "../../stores.js";
  import {External, M} from "../../external";

  export let period = null,
    funnel = null,
    filters = [],
    showArchived = false,
    search = "",
    category = null,
    options = {};

  const dispatch = createEventDispatcher();

  let loading = true,
    initialLoad = true,
    currentPage = 0,
    pagesCount = 0,
    itemsCount = 0,
    table = null,
    selection = null;

  let tableColumns = [
    { key: "image", type: "image", label: "", imageClass: "product-image-item", class: "flex-none product-td-image-wrapper flex align-center justify-center" },
    {
      key: "title",
      label: M.SAMPLE_PRODUCT_NAME_TEXT,
      class: "flex-1 mw-300",
      row: "modified",
      rowLabel: function (row, column) {
        return M["STORE_PRODUCT_" + row.type];
      }
    },
    { key: "priceStr", label: M.PRICE, class: "flex-none mw-140", style: "font-size: 15px" },
    { key: "quantityStr", label: M.INVENTORY, class: "flex-none mw-140", style: "font-size: 15px" },
    { key: "sku", label: M.SKU, class: "flex-none mw-180", style: "font-size: 15px" }
  ];

  let tableData = {
    columns: tableColumns,
    rows: []
  };

  let setRequestFilter = (request) => {
    let settings = External.WebsiteSettings && External.WebsiteSettings.getSettings ? (External.WebsiteSettings.getSettings() || {}) : {};
    let defaultLocale = settings.websiteLanguage || (External.Website ? External.Website.websiteLanguage : '') || 'en';
    let currentLocale = (External.Website && (External.Website.currentEditLocale || External.Website.defaultEditLocale)) || defaultLocale;
    request = request || {};
    request.search = search;
    request.category = category ? category.id : -1;
    request.locale = currentLocale;
    return request;
  };

  let load = async (page) => {
    if (loading && !initialLoad) {
      return;
    }
    initialLoad = false;

    if (page === 0) {
      tableData.rows = [];
      selection = null;
    } else if (currentPage + 1 >= pagesCount) {
      return;
    }
    loading = true;

    let request = setRequestFilter({
      page: page
    });

    let response = await makeWebsiteRequest({
      action: "getStoreProducts",
      method: "post",
      data: request
    });

    loading = false;
    if (!response.data) return;

    currentPage = page;
    pagesCount = response.data.pagesCount;
    itemsCount = response.data.count;

    response.data.items.forEach((item) => {
      let image = External.resources + "/images/image-avatar.png";
      if (item.images && item.images.length) {
        image = External.getThumbnailsFromBase(item.images[0].base, "72x50", true) + item.images[0].name;
      }
      item.image = image;

      let price = item.isOnSale ? item.salePrice : item.price;
      let pricePrefix = item.isCompositePrice ? M.FROM_RANGE + " " : "";
      item.priceStr = pricePrefix + External.Tools.currencyToStrWithSymbol(price);
      item.quantityStr = item.quantity ? pricePrefix + item.quantity : "-";
    });

    if (page === 0) {
      dispatch("itemsloaded", {
        count: itemsCount,
        label: M.ALL_PRODUCTS
      });
    }
    if (table) table.addRows(response.data.items);
  };

  let onNext = () => {
    load(currentPage + 1);
  };

  let onClickRow = (row) => {
    if (options.click === false) {
      return;
    }

    if (options.openProductsIn === "popup") {
      External.UITools.editProductInPopup(row.id, ignore);
    } else {
      External.CPanel.goToUrl("/me/store/products/" + row.id);
    }
  };

  export function reload() {
    load(0);
  }

  let getSelectedItemsCount = () => {
    if (selection !== null) {
      if (selection.all) return itemsCount - selection.unselected.length;
      else return selection.selected.length;
    }
    return itemsCount;
  };

  export function onAddProduct() {
    External.Tools.showWindow({
      title: "",
      width: 800,
      isModal: false,
      customClass: "store-add-new-product",
      markup: External.Templates.get("storeProductAddNewMarkup"),
      action: {
        onShow: function (popup, context) {
          context.find("a").click(function () {
            let anchor = External.$(this),
              type = anchor.data("type"),
              productType = External.STORE_PRODUCT_TYPE.PHYSICAL_PRODUCT;

            switch (type) {
              case "phisical":
                productType = External.STORE_PRODUCT_TYPE.PHYSICAL_PRODUCT;
                break;
              case "digital":
                productType = External.STORE_PRODUCT_TYPE.DIGITAL_PRODUCT;
                break;
              case "service":
                productType = External.STORE_PRODUCT_TYPE.SERVICE;
                break;
              case "membership":
                productType = External.STORE_PRODUCT_TYPE.MEMBERSHIP;
                break;
              case "paywall":
                productType = External.STORE_PRODUCT_TYPE.PAYWALL;
                break;
            }
            anchor.tooltip("hide");
            popup.close();
            External.CPanel.goToUrl(External.Website.virtualPagePrefix + "/store/products/", true, productType);
          });
        }
      }
    });
  }

  export function deleteSelected() {
    let itemsToDelete = getSelectedItemsCount();
    External.Tools.prompt(
      M.DELETE,
      External.format(M.DELETE_PRODUCTS, "count", itemsToDelete),
      async function () {
        let request = setRequestFilter({
          selection: JSON.stringify(selection)
        });

        let response = await makeWebsiteRequest({
          action: "deleteStoreProducts",
          method: "post",
          data: request
        });

        await load(0);
      },
      ignore,
      M.YES,
      M.NO
    );
  }

  onMount(() => {
    load(0);
  });
</script>

{#if $AppState.activeImports && $AppState.activeImports.indexOf("IMPORT_STORE_PRODUCTS") > -1}
  <div class="col20 f-left import-message">{M.IMPORT_IN_PROGRESS}</div>
{/if}
<Table
  bind:this={table}
  activeColumns={tableData.columns}
  data={tableData.rows}
  loading={loading}
  hideHeadOnLoad={true}
  hasSelection={options.selection !== false}
  bind:selection
  onClick={onClickRow}
  nextPage={onNext}
  tableClass={"orders-scrollable-table"}>
  <div class="text-center col20 f-left" style="padding: 70px 30px; box-sizing: border-box;">
    <h3 class="col20 f-left t-center mb-15">{M.NO_PRODUCTS_YET}</h3>
  </div>
</Table>
